/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import i18n from '@/shared/i18n';

/**
 * Maps an i18next language tag to a BCP-47 locale whose digit grouping and
 * decimal conventions match the product's supported languages.
 *
 * Brazilian Portuguese and Spanish both group with "." and use "," for decimals;
 * English groups with "," and uses "." for decimals.
 *
 * @param {string} [language] - The active i18next language tag (defaults to the current one).
 * @returns {string} A BCP-47 locale tag suitable for `Intl.NumberFormat`.
 */
export function resolveNumberLocale(language?: string): string {
  const lang = (language ?? i18n.language ?? 'pt-BR').toLowerCase();
  if (lang.startsWith('en')) return 'en-US';
  if (lang.startsWith('es')) return 'es-ES';
  return 'pt-BR';
}

/**
 * Formats a numeric value with the active locale's grouping/decimal separators
 * and a fixed two-decimal precision (e.g. `1.234,56` in pt-BR, `1,234.56` in en-US).
 *
 * @param {number} value - The numeric value to format.
 * @param {string} [language] - The active i18next language tag (defaults to the current one).
 * @returns {string} The locale-formatted number string with two decimals.
 */
export function formatLocalizedNumber(value: number, language?: string): string {
  return new Intl.NumberFormat(resolveNumberLocale(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formats a stored numeric value for display in a masked input. An empty value
 * (`undefined`/`null`) yields an empty string so placeholders remain visible.
 *
 * @param {number | null | undefined} value - The stored numeric value.
 * @param {string} [language] - The active i18next language tag.
 * @returns {string} The locale-formatted two-decimal string, or `""` when empty.
 */
export function formatStoredNumber(value: number | null | undefined, language?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return formatLocalizedNumber(value, language);
}

/**
 * Parses raw keystrokes from a masked numeric input into a stored number, treating
 * all typed digits as cents (so "55000000" becomes 550000.00). Returns `undefined`
 * when no digits were typed, allowing the field to clear back to empty.
 * Optional `maxDigits` truncates surplus keystrokes before conversion.
 *
 * @param {string} raw - The raw input string from the change event.
 * @param {number} [maxDigits] - Maximum number of digits to keep.
 * @returns {number | undefined} The parsed numeric value, or `undefined` when empty.
 */
export function parseMaskedNumber(raw: string, maxDigits?: number): number | undefined {
  let digits = raw.replace(/\D+/g, '');
  if (digits === '') return undefined;
  if (maxDigits !== undefined) digits = digits.slice(0, maxDigits);
  return Number(digits) / 100;
}

/**
 * Formats a stored integer for display without thousands grouping and without
 * decimal places (e.g. year of construction `2007`, not `2.007` in pt-BR).
 * Returns `""` when empty. Grouping is disabled because integer form fields
 * (years, floors, room counts, lease months) are short identifiers, not currency.
 *
 * @param {number | null | undefined} value - The stored integer value.
 * @param {string} [language] - The active i18next language tag.
 * @returns {string} The ungrouped integer string, or `""` when empty.
 */
export function formatStoredInteger(value: number | null | undefined, language?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(resolveNumberLocale(language), {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Parses raw keystrokes from an integer masked input — keeps digits only and
 * returns the integer value directly (no cents division). Returns `undefined`
 * when no digits were typed. Optional `maxDigits` truncates surplus keystrokes.
 *
 * @param {string} raw - The raw input string from the change event.
 * @param {number} [maxDigits] - Maximum number of digits to keep.
 * @returns {number | undefined} The parsed integer, or `undefined` when empty.
 */
export function parseMaskedInteger(raw: string, maxDigits?: number): number | undefined {
  let digits = raw.replace(/\D+/g, '');
  if (digits === '') return undefined;
  if (maxDigits !== undefined) digits = digits.slice(0, maxDigits);
  return Number(digits);
}

/**
 * Formats a percentage for display with locale separators and up to two
 * fraction digits (e.g. `5,5` in pt-BR). Empty values yield `""`.
 *
 * @param {number | null | undefined} value - The stored percentage.
 * @param {string} [language] - The active i18next language tag.
 * @returns {string} The locale-formatted percentage string, or `""` when empty.
 */
export function formatStoredPercentage(value: number | null | undefined, language?: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(resolveNumberLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Keeps digits and a single decimal separator (`,` or `.`) so the user can type
 * values like `5,5` without letters or repeated commas surviving.
 *
 * @param {string} raw - The raw keystrokes from the percentage input.
 * @returns {string} The sanitized display string.
 */
export function sanitizePercentageInput(raw: string): string {
  let cleaned = raw.replace(/[^\d.,]/g, '');
  if (cleaned.includes(',') && cleaned.includes('.')) {
    cleaned = cleaned.replace(/\./g, '');
  }
  const sepMatch = /[.,]/.exec(cleaned);
  if (!sepMatch || sepMatch.index === undefined) return cleaned;
  const head = cleaned.slice(0, sepMatch.index + 1);
  const tail = cleaned.slice(sepMatch.index + 1).replace(/[.,]/g, '');
  return head + tail.slice(0, 2);
}

/**
 * Normalizes a sanitized percentage string to a decimal-point number string.
 *
 * @param cleaned - Output of {@link sanitizePercentageInput}.
 * @returns Dot-decimal numeric string.
 */
function normalizePercentageToken(cleaned: string): string {
  if (cleaned.includes(',')) {
    return cleaned.replace(/\./g, '').replace(',', '.');
  }
  return cleaned;
}

/**
 * Clamps a finite percentage into the inclusive 0–100 range.
 *
 * @param value - Parsed numeric percentage.
 * @returns Clamped percentage.
 */
function clampPercentage(value: number): number {
  if (value > 100) return 100;
  if (value < 0) return 0;
  return value;
}

/**
 * Parses a percentage typed with locale decimals (`5,5` or `5.5`). Grouping
 * dots before a comma are stripped (BR style). Returns `undefined` when empty.
 *
 * @param {string} raw - The raw input string from the change event.
 * @returns {number | undefined} The parsed percentage, or `undefined` when empty.
 */
export function parseMaskedPercentage(raw: string): number | undefined {
  const cleaned = sanitizePercentageInput(raw);
  if (cleaned === '' || cleaned === ',' || cleaned === '.') return undefined;
  const value = Number(normalizePercentageToken(cleaned));
  if (!Number.isFinite(value)) return undefined;
  return clampPercentage(value);
}
